import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card } from "../../components/common/Primitives";
import { Button } from "../../components/ui/button";
import { Bell, Loader2, Save } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const TYPE_META = {
  "mca.status_change": {
    label: "MCA Status Changes",
    hint: "Fired when VenturaZ re-verifies a startup's MCA status (verified / rejected / more-info-required).",
  },
  "mca.new_filing": {
    label: "New MCA Filings",
    hint: "Annual return, board resolutions, or new director filings from MCA.",
  },
  "financial.shift": {
    label: "Financial Shifts (≥10%)",
    hint: "Founder-declared or extracted metrics moved materially versus the previous snapshot.",
  },
  "document.new": {
    label: "New Documents",
    hint: "A new pitch deck, MIS, cap table, or financial doc landed in the startup's vault.",
  },
};

export default function InvestorAlertPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [types, setTypes] = useState([]);
  const [cadence, setCadence] = useState("realtime");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/investor/alert-preferences")
      .then((r) => {
        setPrefs(r.data.prefs || {});
        setTypes(r.data.alert_types || []);
        setCadence(r.data.cadence || "realtime");
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const toggle = (t) => {
    setPrefs((p) => ({ ...p, [t]: !p[t] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/investor/alert-preferences", { prefs, cadence });
      toast.success("Alert preferences saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = prefs ? Object.values(prefs).filter(Boolean).length : 0;

  return (
    <DashboardShell
      title="Alert Preferences"
      subtitle="INVESTOR · CONTINUOUS MONITORING"
    >
      <Toaster richColors position="top-right" />
      <Card testId="alert-prefs-card" className="mb-4">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-md bg-navy/[0.05]">
            <Bell size={18} className="text-navy" />
          </div>
          <div className="flex-1">
            <div className="overline text-ink-muted">How this works</div>
            <p className="text-sm text-ink mt-1 leading-relaxed">
              You&apos;ll receive an in-app notification + email whenever a
              startup you&apos;ve saved, expressed interest in, or opened a deal
              room with fires an alert. Toggle categories off if you want a
              quieter inbox — you can turn them back on anytime.
            </p>
          </div>
        </div>

        {!prefs && (
          <div className="text-ink-muted text-sm">
            <Loader2 className="animate-spin inline mr-2" size={14} /> Loading…
          </div>
        )}
        {prefs && (
          <div className="space-y-3">
            {/* Cadence selector */}
            <div
              className="border border-navy/[0.1] rounded-md p-4 bg-gradient-to-br from-white to-navy/[0.02]"
              data-testid="cadence-selector"
            >
              <div className="overline mb-3">Delivery cadence</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setCadence("realtime")}
                  data-testid="cadence-realtime"
                  className={
                    "text-left rounded-md p-4 border-2 transition " +
                    (cadence === "realtime"
                      ? "border-navy bg-navy/[0.05]"
                      : "border-navy/[0.1] bg-white hover:border-navy/[0.25]")
                  }
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-heading text-ink">⚡ Real-time</div>
                    {cadence === "realtime" && (
                      <span className="inline-flex items-center gap-1 bg-navy text-white text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded">
                        Active
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-muted">
                    Get an in-app notification + email the moment any alert fires. Best for high-conviction investors on a small watchlist.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setCadence("digest")}
                  data-testid="cadence-digest"
                  className={
                    "text-left rounded-md p-4 border-2 transition " +
                    (cadence === "digest"
                      ? "border-gold bg-gold/[0.08]"
                      : "border-navy/[0.1] bg-white hover:border-navy/[0.25]")
                  }
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-heading text-ink">📬 Sunday digest only</div>
                    {cadence === "digest" && (
                      <span className="inline-flex items-center gap-1 bg-gold text-navy text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded">
                        Active
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-muted">
                    Skip mid-week noise. Every Sunday 07:00 UTC, one email bundles the whole week&apos;s alerts. Best for heavy watchers who don&apos;t want interrupts.
                  </div>
                </button>
              </div>
            </div>

            {/* Type toggles */}
            {types.map((t) => {
              const meta = TYPE_META[t] || { label: t, hint: "" };
              const enabled = !!prefs[t];
              return (
                <div
                  key={t}
                  className={
                    "border rounded-md p-4 flex items-start gap-4 " +
                    (enabled ? "border-navy/[0.1] bg-white" : "border-navy/[0.06] bg-navy/[0.02] opacity-70")
                  }
                  data-testid={`alert-pref-row-${t}`}
                >
                  <div className="flex-1">
                    <div className="font-heading text-ink text-base">{meta.label}</div>
                    <div className="text-xs text-ink-muted mt-0.5">{meta.hint}</div>
                    <div className="mt-1 text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                      {t}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggle(t)}
                    role="switch"
                    aria-checked={enabled}
                    data-testid={`alert-pref-toggle-${t}`}
                    className={
                      "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition " +
                      (enabled ? "bg-navy" : "bg-navy/20")
                    }
                  >
                    <span
                      className={
                        "inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-1 " +
                        (enabled ? "translate-x-6" : "translate-x-1")
                      }
                    />
                  </button>
                </div>
              );
            })}

            <div className="flex items-center justify-between border-t border-navy/[0.06] pt-4 mt-4">
              <div className="text-xs text-ink-muted font-mono">
                {enabledCount} of {types.length} categories enabled
              </div>
              <Button onClick={save} disabled={saving} data-testid="alert-prefs-save">
                {saving ? (
                  <Loader2 className="animate-spin" size={14} />
                ) : (
                  <>
                    <Save size={14} className="mr-2" />
                    Save Preferences
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </DashboardShell>
  );
}
